// Transfer (Stage 4.8f.4a, mockup "Transfer"): the engine moves the call (4.8f.2 — server/ai/phoneRoutes.js → call-engine
// callcontrol.ts). Pick a colleague (with how they are now), a department (who is free in it), or type a number; then "talk first"
// (the customer waits on music while you speak with them; complete, switch, or cancel) or "transfer now". Nobody answers: the
// customer comes back to you — never left waiting.
import { useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, InputBase, Typography, CircularProgress } from "@mui/material";
import { Search, Users, X, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { callAction, myLiveCalls, targetOf, transferTargets } from "../pages/agent/phoneApi";
import { Initial, WithDot, Chip } from "./phoneUi";
import { fmtDur, useSince } from "./phoneFormat";

const WHY = new Set(["no_answer", "cancelled", "unknown_target", "target_hangup", "dnd", "nobody_available"]);

export default function PhoneTransfer({ customer, onClose }) {
  const { t } = useTranslation("phone");
  const [call, setCall] = useState(null);
  const [targets, setTargets] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);   // { to, label }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let alive = true;
    const look = () => myLiveCalls().then((r) => { if (alive) setCall((r?.calls ?? [])[0] ?? null); }).catch(() => {});
    look();
    const id = setInterval(look, 1500);
    transferTargets().then((r) => { if (alive) setTargets(r); }).catch(() => {});
    return () => { alive = false; clearInterval(id); };
  }, []);

  const tr = call?.transfer ?? null;
  const last = call?.last ?? null;
  const why = (r) => (WHY.has(r) ? t(`twhy.${r}`) : t(`why.${r}`, { defaultValue: t("twhy.failed") }));
  useEffect(() => {
    if (last && !tr && last.outcome !== "completed") setMsg(t("transferNotMade", { to: last.to, why: why(last.outcome) }));
  }, [last?.at]); // eslint-disable-line react-hooks/exhaustive-deps
  const consultFor = useSince(tr?.stage === "consulting" ? tr.startedAt : null);
  const heldFor = useSince(tr ? tr.startedAt : null);

  const go = async (mode) => {
    if (!call || !picked) return;
    setBusy(true); setMsg("");
    try { await callAction(call.id, "transfer", { to: picked.to, mode }); }
    catch (e) { setMsg(t("transferRefused", { why: why(e?.response?.data?.reason) })); }
    finally { setBusy(false); }
  };
  const act = async (action) => {
    if (!call) return;
    setBusy(true);
    try { await callAction(call.id, action); } catch (e) { setMsg(t("transferRefused", { why: why(e?.response?.data?.reason) })); }
    finally { setBusy(false); }
  };

  const needle = q.trim().toLowerCase();
  const people = useMemo(() => (targets?.people ?? []).filter((p) => !needle || `${p.name} ${p.ext}`.toLowerCase().includes(needle)), [targets, needle]);
  const depts = useMemo(() => (targets?.departments ?? []).filter((d) => !needle || d.name.toLowerCase().includes(needle)), [targets, needle]);
  const typed = q.replace(/[\s\-().]/g, "");
  const typedOk = /^\+?\d{2,16}$/.test(typed);

  const shell = (children) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", p: "12px 16px", borderTop: "1px solid var(--line-100)", background: "var(--surface-card)" }}>{children}</Box>
  );

  // The engine is ringing the other one.
  if (tr?.stage === "ringing") return shell(
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={14} sx={{ color: "var(--brand-violet)" }} />
        <Typography role="status" sx={{ flex: 1, font: "600 13px/1.35 var(--font-ui)", color: "var(--ink-800)" }}>{t("transferRinging", { to: tr.to })}</Typography>
      </Box>
      <ButtonBase disabled={busy} onClick={() => act("cancel")} sx={{ minHeight: 44, borderRadius: "12px", border: "1px solid var(--line-200)", font: "700 13px/1 var(--font-ui)", color: "var(--ink-800)" }}>{t("cancelBack")}</ButtonBase>
    </>,
  );

  // Talking first: the customer on hold, the colleague on the line.
  if (tr?.stage === "consulting") return shell(
    <>
      <Typography sx={{ font: "800 15px/1.2 var(--font-ui)", color: "var(--ink-900)" }}>{t("handingOver")}</Typography>
      <Typography sx={{ font: "500 12px/1.4 var(--font-ui)", color: "var(--ink-600)" }}>{tr.talkingTo === "peer" ? t("backWithCustomer", { to: tr.to }) : t("talkingWith", { to: tr.to })}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px 10px", borderRadius: "12px", background: "var(--surface-2)" }}>
        <Initial name={customer} size={32} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography dir="auto" noWrap sx={{ font: "700 13px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)" }}>{customer}</Typography>
          <Typography sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{tr.talkingTo === "peer" ? t("talkingWithYou") : t("onHoldMusic")} · {fmtDur(heldFor)}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px 10px", borderRadius: "12px", background: "var(--c-violet-bg)" }}>
        <Initial name={tr.to} size={32} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography dir="auto" noWrap sx={{ font: "700 13px/1.2 var(--font-ui)" }}>{tr.to}</Typography>
          <Typography sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{tr.talkingTo === "peer" ? t("onHold") : t("talkingWithYou")} · {fmtDur(consultFor)}</Typography>
        </Box>
      </Box>
      <ButtonBase disabled={busy} onClick={() => act("complete")} sx={{ minHeight: 44, borderRadius: "12px", background: "var(--grad-brand-135)", boxShadow: "var(--shadow-cta)", color: "#fff", font: "700 14px/1 var(--font-ui)" }}>{t("completeTransfer")}</ButtonBase>
      <Box sx={{ display: "flex", gap: "8px" }}>
        <ButtonBase disabled={busy} onClick={() => act("swap")} sx={{ flex: 1, minHeight: 44, borderRadius: "12px", border: "1px solid var(--line-200)", font: "700 13px/1 var(--font-ui)", color: "var(--ink-800)" }}>{t("switchBetween")}</ButtonBase>
        <ButtonBase disabled={busy} onClick={() => act("cancel")} sx={{ flex: 1, minHeight: 44, borderRadius: "12px", border: "1px solid var(--line-200)", font: "700 13px/1 var(--font-ui)", color: "var(--ink-800)" }}>{t("cancelBack")}</ButtonBase>
      </Box>
    </>,
  );

  // Choosing.
  return shell(
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ flex: 1, font: "800 15px/1.2 var(--font-ui)", color: "var(--ink-900)" }}>{t("transferTitle")}</Typography>
        <ButtonBase onClick={onClose} aria-label={t("close")} sx={{ width: 36, height: 36, borderRadius: "10px", color: "var(--ink-500)" }}><X size={18} aria-hidden="true" /></ButtonBase>
      </Box>
      <Typography sx={{ font: "500 12px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t("transferStays")}</Typography>
      {!call ? <Typography role="status" sx={{ font: "500 13px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t("findingCall")}</Typography> : (
        <>
          <Box component="label" sx={{ display: "flex", alignItems: "center", gap: "8px", height: 42, px: "12px", border: "1px solid var(--line-200)", borderRadius: "12px", background: "var(--surface-2)" }}>
            <Search size={17} strokeWidth={1.8} aria-hidden="true" style={{ color: "var(--ink-500)" }} />
            <InputBase autoFocus value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }} placeholder={t("transferSearch")} inputProps={{ "aria-label": t("transferSearch") }}
              sx={{ flex: 1, minWidth: 0, font: "500 14px/1 var(--font-ui)" }} />
          </Box>
          <Box sx={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {typedOk && (
              <ButtonBase onClick={() => setPicked({ to: targetOf(typed), label: typed })} aria-pressed={picked?.label === typed}
                sx={{ justifyContent: "flex-start", gap: "10px", p: "8px", borderRadius: "10px", background: picked?.label === typed ? "var(--c-violet-bg)" : "transparent" }}>
                <Box aria-hidden="true" sx={{ width: 32, height: 32, borderRadius: "50%", display: "grid", placeItems: "center", background: "var(--surface-2)", color: "var(--ink-600)" }}><Phone size={16} /></Box>
                <Typography sx={{ font: "700 13px/1.2 var(--font-ui)" }}><span dir="ltr">{typed}</span></Typography>
              </ButtonBase>
            )}
            {depts.map((d) => {
              const on = picked?.to === `dept:${d.name}`;
              return (
                <ButtonBase key={`d-${d.name}`} onClick={() => setPicked({ to: `dept:${d.name}`, label: d.name })} aria-pressed={on}
                  sx={{ justifyContent: "flex-start", gap: "10px", p: "8px", borderRadius: "10px", background: on ? "var(--c-violet-bg)" : "transparent", textAlign: "start" }}>
                  <Box aria-hidden="true" sx={{ width: 32, height: 32, borderRadius: "10px", display: "grid", placeItems: "center", background: "var(--c-cyan-bg)", color: "var(--c-cyan)" }}><Users size={16} /></Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ font: "700 13px/1.2 var(--font-ui)" }}>{d.name}</Typography>
                    <Typography noWrap sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{d.available ? t("deptAvailable", { count: d.available }) : t("deptNobody")}</Typography>
                  </Box>
                </ButtonBase>
              );
            })}
            {people.map((p) => {
              const on = picked?.to === `ext:${p.ext}`;
              return (
                <ButtonBase key={`p-${p.ext}`} onClick={() => setPicked({ to: `ext:${p.ext}`, label: p.name })} aria-pressed={on} disabled={p.status === "dnd"}
                  sx={{ justifyContent: "flex-start", gap: "10px", p: "8px", borderRadius: "10px", background: on ? "var(--c-violet-bg)" : "transparent", textAlign: "start", opacity: p.status === "dnd" ? 0.5 : 1 }}>
                  <WithDot status={p.status}><Initial name={p.name} size={32} /></WithDot>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography dir="auto" noWrap sx={{ font: "700 13px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)" }}>{p.name}</Typography>
                    <Typography noWrap sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{t(`status.${p.status}`, { defaultValue: t("status.unknown") })} · {t("extShort", { ext: p.ext })}</Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
          {picked && (
            <Typography sx={{ font: "500 12px/1.45 var(--font-ui)", color: "var(--ink-600)" }}>
              {t("talkFirstExplain", { to: picked.label })} {t("transferNowExplain", { to: picked.label })}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: "8px" }}>
            <ButtonBase disabled={!picked || busy} onClick={() => go("attended")} sx={{ flex: 1, minHeight: 44, borderRadius: "12px", border: "1px solid var(--line-200)", font: "700 13px/1 var(--font-ui)", color: "var(--ink-800)", opacity: picked ? 1 : 0.5 }}>{t("talkFirst")}</ButtonBase>
            <ButtonBase disabled={!picked || busy} onClick={() => go("blind")} sx={{ flex: 1, minHeight: 44, borderRadius: "12px", background: picked ? "var(--grad-brand-135)" : "var(--line-200)", color: "#fff", font: "700 13px/1 var(--font-ui)" }}>{t("transferNow")}</ButtonBase>
          </Box>
        </>
      )}
      {msg && <Chip tone="warn" sx={{ py: "6px", alignSelf: "stretch" }}><span role="status">{msg}</span></Chip>}
    </>,
  );
}
